import {
  PopoverTrigger,
  PopoverContent,
  Popover,
  PopoverAnchor,
} from './ui/popover';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { ItemsListEmptyState } from './ItemsListEmptyState';
import { cn } from '../utils';
import { useState } from 'react';

type Props<T> = {
  triggerIcon: React.ReactNode;
  options: T[];
  renderOption: (option: T) => React.ReactNode;
  onSelect: (option: T) => void;
  getOptionKey: (option: T) => string;
  children: (trigger: React.ReactNode) => React.ReactNode;
  contentClassName?: string;
  disabledTrigger?: boolean;
};

export function AnchorDropdown<T>({
  triggerIcon,
  options,
  renderOption,
  getOptionKey,
  onSelect,
  children,
  contentClassName,
  disabledTrigger = false,
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleSelect = (option: T) => {
    onSelect(option);
    setOpen(false);
  };

  const trigger = (
    <PopoverTrigger asChild>
      <Button size="icon-button" variant="icon" disabled={disabledTrigger}>
        {triggerIcon}
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{children(trigger)}</PopoverAnchor>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] p-0 max-h-64 overflow-y-auto rounded-[1.6rem]',
          contentClassName
        )}
        // it enables scrolling on the popover content
        onWheel={e => e.stopPropagation()}>
        {options.length === 0 && (
          <ItemsListEmptyState
            title={t('common.noResults')}
            description={t('common.noResultsDescription')}
            className="py-[4.8rem] px-[3.2rem]"
          />
        )}

        <div className="p-[1.2rem]">
          {options.map(option => (
            <div
              key={getOptionKey(option)}
              onClick={() => handleSelect(option)}
              className="cursor-pointer">
              {renderOption(option)}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
